import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {assertAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  TemplatePublishingRecordsQuery,
  TemplatePublishingRecordsQueryVariables,
} from 'template-collectibles/publishing-ui/graphql/__generated/TemplatePublishingRecordsQuery';
import {TEMPLATE_PUBLISHING_RECORDS_QUERY} from 'template-collectibles/publishing-ui/graphql/TemplatePublishingRecordsQuery';
import {TemplatePublishingRecord} from 'template-collectibles/publishing-ui/graphql/types';

/**
 * The template's publishing records and how the open version relates to them. `loading` and `error` let
 * callers choose how to render, or whether to allow edits, while the status is unknown.
 */
export type TemplateVersionPublishingState =
  | {type: 'loading'}
  | {type: 'error'}
  | {
      type: 'loaded';
      publishingRecords: Array<TemplatePublishingRecord>;
      /** The open version's record, or null if the open version has never been published. */
      openVersionPublishingRecord: TemplatePublishingRecord | null;
      refetch: () => Promise<unknown>;
    };

export default function useTemplateVersionPublishingState(
  templateId: string,
  openVersionApiId: string
): TemplateVersionPublishingState {
  const result = useBenchlingQuery<TemplatePublishingRecordsQuery, TemplatePublishingRecordsQueryVariables>(
    TEMPLATE_PUBLISHING_RECORDS_QUERY,
    // Keeps the previous records while refetching after a publish or withdraw, rather than reporting loading.
    {variables: {templateId}, renderCachedDataWhenRefetching: true}
  );
  if (result.type === QueryResultType.PENDING) {
    return {type: 'loading'};
  } else if (result.type === QueryResultType.ERROR) {
    return {type: 'error'};
  }

  const {template} = result.data;
  assert(!isInaccessible(template), 'Expected template to be accessible.');
  const publishingRecords = assertAllAccessible(template.publishingRecords);
  return {
    type: 'loaded',
    publishingRecords,
    openVersionPublishingRecord:
      publishingRecords.find((pr) => pr.publishableVersionId === openVersionApiId) ?? null,
    refetch: result.refetch,
  };
}
